"use client";

import {
  Dispatch,
  ReactNode,
  createContext,
  useReducer,
  useMemo,
  useCallback,
  useEffect,
} from "react";

export type AppStateProps = {
  acceptedConsent: boolean;
  consentModalOpen: boolean;
  assetId: string | null;
  userData: {
    email: string;
    name: string;
  } | null;
  toast: {
    open: boolean;
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null;
};

type AppContextProps = {
  state: AppStateProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setState: Dispatch<{ type: string; payload: any }>;
  onConsentModalOpenChange: (open: boolean, assetId: string | null) => void;
  onAcceptedConsent: (email: string, name: string) => void;
  onVerifyConsent: (assetId: string) => Promise<boolean | null>;
  onToastChange: (open: boolean) => void;
};

const initialAppState: AppStateProps = {
  acceptedConsent: false,
  consentModalOpen: false,
  assetId: null,
  userData: null,
  toast: {
    open: false,
    message: "",
    type: "success",
  },
};

type AppProviderProps = {
  children: ReactNode;
};

export const AppContext = createContext<AppContextProps>({
  state: initialAppState,
  setState: () => {},
  onConsentModalOpenChange: () => {},
  onAcceptedConsent: () => {},
  onVerifyConsent: async (assetId: string) => null,
  onToastChange: () => {},
});

const CONSENT_STORAGE_KEY = "moises:consent";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function appReducer(state: AppStateProps, action: { type: string; payload?: any }) {
  switch (action.type) {
    case "CONSENT_MODAL_OPEN": {
      return {
        ...state,
        consentModalOpen: action.payload.open,
        assetId: action.payload.assetId,
      };
    }
    case "ACCEPTED_CONSENT": {
      return {
        ...state,
        acceptedConsent: true,
        consentModalOpen: false,
        userData: { name: action.payload.name, email: action.payload.email },
        toast: { ...state.toast, ...action.payload.toast },
      };
    }
    case "TOAST_CHANGE": {
      return {
        ...state,
        toast: { ...state.toast, open: action.payload.open },
      };
    }
    case "TOAST_OPEN": {
      return {
        ...state,
        toast: action.payload,
      };
    }
    default: {
      throw Error("Unknown action: " + action.type);
    }
  }
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);

  const onConsentModalOpenChange = useCallback(
    (open: boolean, assetId: string | null) => {
      dispatch({ type: "CONSENT_MODAL_OPEN", payload: { open, assetId } });
    },
    [],
  );

  const onVerifyConsent = useCallback(
    async (assetId: string): Promise<boolean | null> => {
      if (state.acceptedConsent) {
        const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (!consent) return null;
        const { email, name } = JSON.parse(consent);
        if (!email || !name) return null;

        const response = await fetch("/api/user-consent", {
          method: "POST",
          body: JSON.stringify({ name, email, resourceId: assetId }),
        });

        if (!response.ok) return null;
        const { data } = await response.json();
        if (!data || !data.success) return null;

        dispatch({
          type: "TOAST_OPEN",
          payload: {
            open: true,
            message: "Request successfully completed. Please check your email inbox!",
            type: "success",
          },
        });

        return true;
      } else {
        onConsentModalOpenChange(true, assetId);
        return null;
      }
    },
    [state, onConsentModalOpenChange],
  );

  const onAcceptedConsent = useCallback((email: string, name: string) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ email, name }));
    dispatch({
      type: "ACCEPTED_CONSENT",
      payload: {
        email,
        name,
        toast: {
          open: true,
          message: "Request successfully completed. Please check your email inbox!",
          type: "success",
        },
      },
    });
  }, []);

  const onToastChange = useCallback((open: boolean) => {
    dispatch({ type: "TOAST_CHANGE", payload: { open } });
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (consent) {
      const { email, name } = JSON.parse(consent);
      if (email && name) {
        dispatch({ type: "ACCEPTED_CONSENT", payload: { email, name } });
      }
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      setState: dispatch,
      onConsentModalOpenChange,
      onAcceptedConsent,
      onVerifyConsent,
      onToastChange,
    }),
    [state, onConsentModalOpenChange, onAcceptedConsent, onVerifyConsent, onToastChange],
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};
